import {
    useEffect,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type IDirectionProviderProps from "./type";
import type {
    DirectionProviderNativePropsType,
    DirectionProviderWebPropsType
} from "./type";
import {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize
} from "../../core/hooks";
import {
    isLangDirectionConflict
} from "../../helpers/direction";
import {
    warnOnce
} from "../../utils/development";

const DirectionProvider: FC<IDirectionProviderProps> = ({
    direction: directionProp,
    children,
    style,
    lang,
    ...props
}) => {
    const {
        direction: inheritedDirection
    } = NCoreUIKitLocalize.useContext();

    const direction = directionProp ?? inheritedDirection;

    const isLangConflict = Platform.OS === "web" && isLangDirectionConflict({
        direction,
        lang
    });

    const {
        container: containerDynamicStyle
    } = useStyles({
        direction: directionProp
    });

    const webProps: DirectionProviderWebPropsType = Platform.OS === "web" ? {
        lang: isLangConflict ? undefined : lang,
        dir: directionProp
    } : {};

    const nativeProps: DirectionProviderNativePropsType = Platform.OS === "ios" && lang ? {
        accessibilityLanguage: lang
    } : {};

    useEffect(() => {
        if(!isLangConflict) {
            return;
        }

        warnOnce({
            message: `DirectionProvider: lang "${lang}" does not read ${direction}, so it is not applied. Use a lang whose script direction matches.`
        });
    }, [
        isLangConflict,
        direction,
        lang
    ]);

    return <NCoreUIKitLocalize.DirectionProvider
        direction={direction}
    >
        <View
            {...props}
            {...nativeProps}
            {...webProps}
            style={[
                style,
                containerDynamicStyle
            ]}
        >
            {children}
        </View>
    </NCoreUIKitLocalize.DirectionProvider>;
};
export default DirectionProvider;
